import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import CookieJs from "js-cookie";
import get from "lodash/get";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import coaService from "../../services/coa";
import groupService from "../../services/group";
import plService from "../../services/pl";
import queryDefault from "../../utils/queryDefault";

const formatDate = (value: Dayjs | null, fallback: string) => {
  if (!value || !value.isValid()) {
    return fallback;
  }

  return value.format("YYYY-MM-DD");
};

export default function InputPL() {
  const { id } = useParams();
  const navigate = useNavigate();
  const notification = useNotification();
  const user = JSON.parse(CookieJs.get("USER") || "{}");
  const [transactionDate, setTransactionDate] = useState<Dayjs | null>(dayjs());

  const form = useForm({
    defaultValues: {
      branch_group_id: 0,
      transaction_date: dayjs().format("YYYY-MM-DD"),
      coa_code: "",
      amount: 0,
      description: "",
      accurate_no: "",
    },
  });

  const getBranchGroups = useQuery(
    ["branch-group"],
    () => {
      return groupService.getAllBranchGroup({ keyword: "" });
    },
    {
      ...queryDefault,
    }
  );

  const getCOA = useQuery(
    ["coa-options"],
    () => {
      return coaService.listCOA();
    },
    {
      ...queryDefault,
    }
  );

  useEffect(() => {
    if (!id) {
      return;
    }

    plService.getPL(id).then((response) => {
      const data = get(response, "data", {});
      form.reset({
        branch_group_id: Number(get(data, "branch_group_id", 0)),
        transaction_date: String(get(data, "transaction_date", dayjs().format("YYYY-MM-DD"))),
        coa_code: String(get(data, "coa_code", "")),
        amount: Number(get(data, "amount", 0)),
        description: String(get(data, "description", "")),
        accurate_no: String(get(data, "accurate_no", "")),
      });

      setTransactionDate(dayjs(get(data, "transaction_date", dayjs().format("YYYY-MM-DD"))));
    });
  }, [id, form]);

  const createPL = useMutation("create-pl", plService.createPL, {
    onSuccess: (response) => {
      notification.onOpen({
        message: response?.message || "P&L was successfully created!",
        type: "success",
        position: "top",
      });
      navigate("/app/pl");
    },
    onError: (error: any) => {
      notification.onOpen({
        message: error?.response?.data?.message || "Something went wrong",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const editPL = useMutation(
    "edit-pl",
    ({ payloadId, payload }: { payloadId: string; payload: any }) =>
      plService.updatePL(payloadId, payload),
    {
      onSuccess: (response) => {
        notification.onOpen({
          message: response?.message || "P&L was successfully updated!",
          type: "success",
          position: "top",
        });
        navigate("/app/pl");
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Something went wrong",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const onSubmit = (values: any) => {
    const payload = {
      ...values,
      username: user?.username || user?.name || "",
    };

    if (id) {
      editPL.mutate({ payloadId: id, payload });
      return;
    }

    createPL.mutate(payload);
  };

  return (
    <MainLayout>
      <Breadcrumb
        label={id ? "Edit P&L" : "Create P&L"}
        breadcrumbs={[{ label: "P&L", href: "/app/pl" }]}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 11]}>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal">
                <FormLabel required>Branch Group</FormLabel>
                <Controller
                  name="branch_group_id"
                  control={form.control}
                  render={() => (
                    <Select
                      value={form.getValues().branch_group_id}
                      onChange={(event) => {
                        form.setValue("branch_group_id", Number(event.target.value));
                      }}
                    >
                      <MenuItem value={0}>Select Branch Group</MenuItem>
                      {get(getBranchGroups, "data.data", []).map((item: any) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal">
                <FormLabel required>Transaction Date</FormLabel>
                <Controller
                  name="transaction_date"
                  control={form.control}
                  render={() => (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        inputFormat="DD/MM/YYYY"
                        value={transactionDate}
                        onChange={(newValue) => {
                          setTransactionDate(newValue);
                          form.setValue(
                            "transaction_date",
                            formatDate(newValue, form.getValues().transaction_date)
                          );
                        }}
                        renderInput={(params) => <TextField {...params} size="small" />}
                      />
                    </LocalizationProvider>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal">
                <FormLabel required>COA</FormLabel>
                <Controller
                  name="coa_code"
                  control={form.control}
                  render={() => (
                    <Select
                      value={form.getValues().coa_code}
                      onChange={(event) => {
                        form.setValue("coa_code", String(event.target.value));
                      }}
                    >
                      <MenuItem value="">Select COA</MenuItem>
                      {get(getCOA, "data.data", []).map((item: any) => (
                        <MenuItem key={item.code} value={item.code}>
                          {item.code} - {item.description}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal">
                <FormLabel required>Amount</FormLabel>
                <Controller
                  name="amount"
                  control={form.control}
                  render={({ field: { ref, ...controllerField } }) => (
                    <TextField
                      {...controllerField}
                      type="number"
                      size="small"
                      inputRef={ref}
                      inputProps={{
                        step: "any",
                      }}
                      onChange={(event) => {
                        const value = event.target.value;
                        form.setValue("amount", value === "" ? 0 : Number(value));
                      }}
                    />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth margin="normal">
                <FormLabel>Accurate No</FormLabel>
                <Controller
                  name="accurate_no"
                  control={form.control}
                  render={({ field: { ref, ...controllerField } }) => (
                    <TextField {...controllerField} size="small" inputRef={ref} />
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <FormLabel>Description</FormLabel>
                <Controller
                  name="description"
                  control={form.control}
                  render={({ field: { ref, ...controllerField } }) => (
                    <TextField {...controllerField} size="small" inputRef={ref} multiline rows={3} />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              variant="contained"
              onClick={form.handleSubmit(onSubmit)}
              loading={createPL.isLoading || editPL.isLoading}
            >
              Save
            </LoadingButton>

            <Button variant="outlined" onClick={() => navigate(-1)}>
              Cancel
            </Button>
          </Stack>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
